import {ActionPattern} from '@redux-saga/types';
import {bimap, Either, fold as foldEither, getOrElse, isRight, left, map as mapEither, right} from 'fp-ts/lib/Either';
import {identity} from 'fp-ts/lib/function';
import {pipe} from 'fp-ts/lib/pipeable';
import {Action} from 'redux';
import {call, CallEffect, put, PutEffect, takeLatest} from 'redux-saga/effects';
import unionize, {ofType, UnionOf} from 'unionize';
import {
  Async,
  AsyncError,
  AsyncOperation,
  AsyncPage,
  AsyncResult,
  Loaded,
  Loading,
  LoadingAdditional,
} from '../../Core/models/Async';
import {Page} from '../../Core/models/Page';
import {Validatable} from '../../Core/models/Validatable';
import {ActionWithPayload} from './actionCreator';

export type PageFetchActionBase = {
  existingPage: Page<any>;
  filter: any;
};

export const PageFetchAction = unionize({
  Additional: ofType<PageFetchActionBase>(),
  Initial: ofType<{filter: any}>(),
});

export type PageFetchActionUnion = UnionOf<typeof PageFetchAction>;

export function getPageApiCallGenerator<
  TActionKey extends string,
  TFetchAction extends ActionWithPayload<TActionKey, PageFetchActionUnion>,
  FetchResponse,
  ChangeActionType extends string
>(
  fetchFunction: (fetchAction: TFetchAction) => AsyncOperation<FetchResponse>,
  changeAsyncDataAction: (
    data: AsyncPage<FetchResponse>
  ) => ActionWithPayload<ChangeActionType, AsyncPage<FetchResponse>>
) {
  return function*(action: TFetchAction) {
    const loadingState = put(changeAsyncDataAction(new Loading()));
    const loadingAdditionalState = (data: any) => put(changeAsyncDataAction(new LoadingAdditional(data)));

    yield PageFetchAction.match({
      Additional: additional => loadingAdditionalState(additional.existingPage),
      Initial: () => loadingState,
    })(action.payload);

    const result: AsyncResult<FetchResponse> = yield call(fetchFunction, action);

    yield put(
      pipe(
        result,
        mapEither(r => new Loaded(r)),
        foldEither(changeAsyncDataAction, changeAsyncDataAction)
      )
    );
  };
}

export function getApiCallGenerator<TFetchAction, FetchResponse, ChangeActionType extends string>(
  fetchFunction: (fetchAction: TFetchAction) => AsyncOperation<FetchResponse>,
  changeAsyncDataAction: (data: Async<FetchResponse>) => ActionWithPayload<ChangeActionType, Async<FetchResponse>>
) {
  return function*(action: TFetchAction) {
    yield put(changeAsyncDataAction(new Loading()));

    const result: AsyncResult<FetchResponse> = yield call(fetchFunction, action);

    const loadedResult = pipe(
      result,
      mapEither(r => new Loaded(r)),
      bimap(changeAsyncDataAction, changeAsyncDataAction)
    );

    yield put(pipe(loadedResult, getOrElse(identity)));
  };
}

export const apiCallGenerator = <TFetchAction, TFetchResponse>(
  fetchFunction: (fetchAction: TFetchAction) => AsyncOperation<TFetchResponse>
) =>
  function*(
    action: TFetchAction & {
      payload: {changeAsyncData: (data: Async<TFetchResponse>) => any};
    }
  ) {
    action.payload.changeAsyncData(new Loading());

    const result: Either<AsyncError, TFetchResponse> = yield call(fetchFunction, action);

    pipe(
      result,
      mapEither(r => new Loaded(r)),
      bimap(action.payload.changeAsyncData, action.payload.changeAsyncData)
    );
  };

export function* withLatestPageAction<ChangeActionType extends string>(
  fetchActionType: ActionPattern<Action<any>>,
  generator: (
    action: ActionWithPayload<string, PageFetchActionUnion>
  ) => IterableIterator<CallEffect | PutEffect<ActionWithPayload<ChangeActionType, PageFetchActionUnion>>>
) {
  yield takeLatest(fetchActionType, generator);
}

export const getValidationSaga = <TLeft, TRight extends TLeft>(
  create: (value: string) => TLeft,
  setValue: (value: Validatable<TLeft, TRight>) => ActionWithPayload<any, any>,
  validateApi: (value: string) => AsyncOperation<any>,
  predicate?: (value: string) => boolean
) =>
  function*(action: ActionWithPayload<any, string>) {
    action.payload = action.payload || '';
    if (!predicate || predicate(action.payload)) {
      yield put(setValue(new Loading(left(create(action.payload)))));

      const result: AsyncResult<any> = yield call(validateApi, action.payload);

      const isValid = isRight(result);

      const mapResult = (value: string) => (isValid ? right(create(value) as TRight) : left(create(value)));

      const newValue = new Loaded(mapResult(action.payload));

      yield put(setValue(newValue));
    } else {
      yield put(setValue(new Loaded(left(create(action.payload)))));
    }
  };
